<template>
    <basic-header pretitle="aqfer Graph Management" title="Graph Analytics Report" :compact="true" />
    <div class="container-fluid">

        <div class="row g-3 align-items-center mt-3 mb-4">
            <div class="col-auto">
                <InputField colField="mb-0" :editable="true" label="Report Type" :options="reportTypeOptions"
                    type="select" :modelValue="reportType" @change="reportTypeChange($event)" />
            </div>
            <div class="col-auto">
                <InputField colField="mb-0" :editable="true" label="Metric Type" :options="metricTypeOptions"
                    type="select" :modelValue="metricType" @change="metricTypeChange($event)" />
            </div>
            <div class="col-auto">
                <InputField colField="mb-0" :editable="true" label="Data Type" :options="dataTypeOptions" type="select"
                    :modelValue="dataType" @change="dataTypeChange($event)" />
            </div>
            <div class="col-auto" v-if="metricType === 'n_days'">
                <InputField colField="mb-0" :editable="true" label="Number of Days" type="number"
                    :modelValue="numberOfDays" @change="numberOfDaysChange($event)"
                    :input-config="{ min: 1, max: 365 }" />
            </div>
            <div class="col-auto" v-if="metricType === 'n_days' && graphNameOptions.length > 1">
                <InputField colField="mb-0" :editable="true" label="Graph Name" :options="graphNameOptions"
                    type="select" :modelValue="selectedGraphName" @change="selectedGraphNameChange($event)" />
            </div>
            <div class="col-auto" v-if="metricType === 'event_date'">
                <InputField label="Date" colField="mb-0" :modelValue="selectedDate" type="calendar"
                    :input-config="dateConfig" @update:modelValue="selectedDateChange($event)" />
            </div>
            <div class="col-auto ms-auto" v-if="reportData?.length && metricType !== 'n_days'">
                <div class="dropdown">
                    <button class="btn btn-primary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown"
                        aria-expanded="false">
                        Export
                    </button>
                    <ul class="dropdown-menu">
                        <li><a class="dropdown-item" href="#" @click="exportCSV()">Current View (CSV)</a></li>
                    </ul>
                </div>
            </div>
        </div>

        

        <!-- Chart View for N-Days Metric Type -->
        <div v-if="metricType === 'n_days'">
            <div class="card">
                <div class="card-body">
                    <div v-if="loading" class="text-center">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div v-else-if="reportData?.length" class="row">
                        <div class="col-12">
                            <div v-for="(chartConfig, graphName) in nDaysChartsByGraph" :key="graphName"
                                class="mb-5 chart-view">
                                <h4 class="text-center">{{ graphName }} - {{reportTypeOptions.find(opt => opt.value ===
                                    reportType)?.label || 'Graph Report' }}</h4>
                                <Line :data="chartConfig" :options="chartOptions" />
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center text-muted">
                        No data available for the selected criteria.
                    </div>
                </div>
            </div>
        </div>

        <!-- Table View for Other Metric Types -->
        <div v-else>
            <div class="card">
                <div class="card-body" :class="{ 'table-card-body': reportData?.length }">
                    <div v-if="loading" class="text-center">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <div v-else-if="reportData?.length" class="table-container">
                        <CollapsibleTable id="graph-report-table" :data="reportData" :columns="reportColumns"
                            :groupBy="tableGroupBy" :aggregateCol="tableAggregateCol"
                            :collapsibleCol="tableCollapsibleCol" />
                    </div>
                    <div v-else class="text-center text-muted">
                        No data available for the selected criteria.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BasicHeader from '../../components/BasicHeader.vue'
import InputField from "../../components/InputField.vue";
import CollapsibleTable from "../../components/CollapsibleTable.vue";
import axios from 'axios';
import { mapActions, mapState } from "vuex";
import csv from "../../utils/tools/csv"
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors, LineElement, PointElement } from 'chart.js'
import { Line } from 'vue-chartjs'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors, LineElement, PointElement)

export default {
    components: { Line, BasicHeader, InputField, CollapsibleTable },

    data() {
        return {
            loading: false,
            reportTypeOptions: [
                { label: "Top N Entities", value: "top_n_entities" },
                { label: "Histogram", value: "histogram" },
                { label: "Edges Summary", value: "edges_summary" },
                { label: "Histogram per Type", value: "histogram_per_type" },
                { label: "Entities Summary", value: "entities_summary" },
                { label: "Graph Diff", value: "graph_diff" },
            ],
            metricTypeOptions: [
                { label: "N-Days", value: "n_days" },
                { label: "Specific Date", value: "event_date" },
                { label: "Latest", value: "latest" },
            ],
            metricType: "latest",
            reportType: "top_n_entities",
            dataType: "cluster-graph",
            numberOfDays: 7,
            selectedGraphName: null,
            graphNameOptions: [],
            selectedDate: null,
            dateConfig: {
                wrap: true,
                dateFormat: 'Y-m-d'
            },
            reportData: [],
            reportColumns: [],
            apiHeaders: [],
            tableGroupBy: [],
            tableAggregateCol: "neighbor_count",
            tableCollapsibleCol: "graph_name",
            dataTypeMapping: {
                "top_n_entities": [
                    { label: "IDR", value: "idr" },
                    { label: "Deterministic Graph", value: "deterministic-graph" },
                    { label: "Cluster Graph", value: "cluster-graph" }
                ],
                "histogram": [
                    { label: "IDR", value: "idr" },
                    { label: "Cluster Graph", value: "cluster-graph" }
                ],
                "edges_summary": [
                    { label: "IDR", value: "idr" },
                    { label: "Deterministic Graph", value: "deterministic-graph" }
                ],
                "histogram_per_type": [
                    { label: "Cluster Graph", value: "cluster-graph" }
                ],
                "entities_summary": [
                    { label: "IDR", value: "idr" }
                ],
                "graph_diff": [
                    { label: "Deterministic and Deterministic Graph", value: "deterministic-and-deterministic-graph" },
                    { label: "Deterministic and Cluster Graph", value: "deterministic-and-cluster-graph" },
                    { label: "Cluster and Cluster Graph", value: "cluster-and-cluster-graph" }
                ]
            },
            chartOptions: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: {
                        forceOverride: true
                    },
                    legend: {
                        display: true,
                        position: 'top',
                        maxHeight: 100
                    }
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Count'
                        }
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Entity Count'
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Date'
                        }
                    }
                },
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                elements: {
                    point: {
                        radius: 3,
                        hoverRadius: 5
                    }
                }
            },
        }
    },

    methods: {
        async getGraphReport() {
            this.loading = true;
            this.reportData = [];
            this.reportColumns = [];
            this.apiHeaders = [];

            try {
                const postArguments = {
                    cid: `${this.cid}`
                };

                // Only add data_type for report types that require it
                if (this.reportType !== 'entities_summary' && this.reportType !== 'histogram_per_type') {
                    postArguments.data_type = this.dataType;
                }

                // Add parameters based on metric type
                if (this.metricType === 'n_days') {
                    postArguments.n = this.numberOfDays.toString();
                } else if (this.metricType === 'event_date' && this.selectedDate) {
                    // Convert date from YYYY-MM-DD to YYYYMMDD format
                    const formattedDate = this.selectedDate.replace(/-/g, '');
                    postArguments.event_date = formattedDate;
                }

                // Special case for edges_summary with n_days
                let endpoint = `${this.reportType}_${this.metricType}`;
                if (this.reportType === 'edges_summary' && this.metricType === 'n_days') {
                    endpoint = 'edges_summary_past_n_days';
                }

                const response = await axios.post(
                    `${this.$store.state.countsUrl}/v2/query/${endpoint}/exec`,
                    {
                        "arguments": postArguments
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`
                        }
                    }
                );

                if (response.data?.data) {
                    const { data, headers } = response.data.data;
                    this.apiHeaders = headers;

                    // Transform data into objects for the table
                    this.reportData = data.map(row => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            // Convert numeric fields to numbers
                            if (header === 'neighbor_count' || header === 'entity_count' ||
                                header.includes('count') || header.includes('value') || header.includes('num')) {
                                obj[header] = parseFloat(row[index]) || 0;
                            } else {
                                obj[header] = row[index];
                            }
                        });
                        return obj;
                    });

                    console.log('Transformed reportData sample:', this.reportData.slice(0, 2));

                    // Create columns for the table
                    this.reportColumns = headers.map(header => ({
                        data: header,
                        title: this.formatHeaderTitle(header)
                    }));

                    // Set up collapsible table configuration based on available headers
                    this.configureCollapsibleTable(headers);

                    // Final validation - ensure we have minimum required config
                    if (!this.tableGroupBy.length) {
                        this.tableGroupBy = [headers[0]];
                        this.tableCollapsibleCol = headers[0];
                    }
                    if (!this.tableAggregateCol) {
                        this.tableAggregateCol = headers[headers.length - 1];
                    }

                    // Extract unique graph names for dropdown
                    if (this.metricType === 'n_days' && headers.includes('graph_name')) {
                        const uniqueGraphNames = [...new Set(data.map(row => row[headers.indexOf('graph_name')]))];
                        this.graphNameOptions = uniqueGraphNames.map(name => ({
                            label: name,
                            value: name
                        }));

                        // Set default selected graph name
                        if (!this.selectedGraphName && uniqueGraphNames.length > 0) {
                            this.selectedGraphName = uniqueGraphNames[0];
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching graph report:', error);
                // Handle error appropriately
            } finally {
                this.loading = false;
            }
        },

        formatHeaderTitle(header) {
            // Convert snake_case to Title Case
            return header.split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },

        exportCSV() {
            this.error = null;
            if (this.reportData.length > 0) {
                const title = `graph-report-${this.reportType}-${this.metricType}`;
                csv.exportCSV(title, this.reportData);
            } else {
                this.error = "No data to export";
            }
        },

        reportTypeChange(newValue) {
            this.reportType = newValue;

            // Set default data type for the new report type
            const availableDataTypes = this.dataTypeMapping[newValue];
            if (availableDataTypes && availableDataTypes.length > 0) {
                // Set to first available option if current dataType is not valid for new report type
                const currentDataTypeValid = availableDataTypes.some(option => option.value === this.dataType);
                if (!currentDataTypeValid) {
                    this.dataType = availableDataTypes[0].value;
                }
            }

            this.getGraphReport();
        },

        metricTypeChange(newValue) {
            this.metricType = newValue;

            // Set default date for event_date metric type
            if (newValue === 'event_date' && !this.selectedDate) {
                const today = new Date();
                this.selectedDate = today.toISOString().split('T')[0];
            }

            this.getGraphReport();
        },

        dataTypeChange(newValue) {
            this.dataType = newValue;
            this.getGraphReport();
        },

        numberOfDaysChange(newValue) {
            this.numberOfDays = parseInt(newValue) || 7;
            if (this.metricType === 'n_days') {
                this.getGraphReport();
            }
        },

        selectedGraphNameChange(newValue) {
            this.selectedGraphName = newValue;
        },

        configureCollapsibleTable(headers) {
            // Configure based on report type and available headers
            console.log('Configuring table for:', {
                reportType: this.reportType,
                metricType: this.metricType,
                headers: headers
            });

            // Set up groupBy based on report type and available headers
            if (this.reportType === 'histogram') {
                // For histogram, group by graph_name and job_name if available
                const possibleGroupBy = ['graph_name', 'job_name'];
                this.tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));

                // Use entity_count for histogram aggregation if available, otherwise neighbor_count
                if (headers.includes('entity_count')) {
                    this.tableAggregateCol = 'entity_count';
                } else if (headers.includes('neighbor_count')) {
                    this.tableAggregateCol = 'neighbor_count';
                }
            } else if (this.reportType === 'top_n_entities') {
                // For top entities, group by graph_name and entity_type/entity_domain
                const possibleGroupBy = ['graph_name', 'entity_type', 'entity_domain'];
                this.tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));
                this.tableAggregateCol = 'neighbor_count';
            } else {
                // Generic configuration for other report types
                const possibleGroupBy = ['graph_name', 'job_name'];
                this.tableGroupBy = possibleGroupBy.filter(col => headers.includes(col));

                // Find best numeric column for aggregation
                if (headers.includes('neighbor_count')) {
                    this.tableAggregateCol = 'neighbor_count';
                } else if (headers.includes('entity_count')) {
                    this.tableAggregateCol = 'entity_count';
                } else {
                    const numericCol = headers.find(header =>
                        header.includes('count') || header.includes('value') || header.includes('num')
                    );
                    this.tableAggregateCol = numericCol || headers[headers.length - 1];
                }
            }

            // Set collapsible column (first groupBy column)
            if (this.tableGroupBy.length > 0) {
                this.tableCollapsibleCol = this.tableGroupBy[0];
            } else if (headers.includes('graph_name')) {
                this.tableCollapsibleCol = 'graph_name';
                this.tableGroupBy = ['graph_name']; // Ensure at least one groupBy column
            } else {
                this.tableCollapsibleCol = headers[0];
                this.tableGroupBy = [headers[0]]; // Ensure at least one groupBy column
            }

            console.log('Final CollapsibleTable Config:', {
                reportType: this.reportType,
                groupBy: this.tableGroupBy,
                aggregateCol: this.tableAggregateCol,
                collapsibleCol: this.tableCollapsibleCol,
                headers: headers,
                dataLength: this.reportData.length
            });
        },

        async selectedDateChange(newValue) {
            this.selectedDate = newValue;
            if (this.metricType === 'event_date') {
                await this.getGraphReport();
            }
        },

        generateNDaysChartsByGraph() {
            const chartsByGraph = {};

            // First group by graph_name
            const graphGroups = {};
            this.reportData.forEach(item => {
                const graphName = item.graph_name || 'Unknown Graph';
                if (!graphGroups[graphName]) {
                    graphGroups[graphName] = [];
                }
                graphGroups[graphName].push(item);
            });

            // For each graph, create a chart with individual entities
            Object.keys(graphGroups).forEach(graphName => {
                const graphData = graphGroups[graphName];

                // Group by date within this graph
                const dateGroups = {};
                graphData.forEach(item => {
                    if (item.event_date) {
                        // Format date from YYYYMMDD to YYYY-MM-DD
                        const dateStr = item.event_date.toString();
                        const formattedDate = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;

                        if (!dateGroups[formattedDate]) {
                            dateGroups[formattedDate] = [];
                        }
                        dateGroups[formattedDate].push(item);
                    }
                });

                // Sort dates
                const sortedDates = Object.keys(dateGroups).sort();

                let datasets = [];

                // Check if we have entity_id (for individual entity tracking) or aggregate data
                const hasEntityIds = graphData.some(item => item.entity_id);

                if (hasEntityIds) {
                    // Get all unique entities for this graph
                    const entityIds = [...new Set(graphData.map(item => item.entity_id))];

                    // Create datasets for each entity
                    datasets = entityIds.slice(0, 20).map((entityId, index) => { // Limit to 20 entities for readability
                        const entityData = sortedDates.map(date => {
                            const dayData = dateGroups[date] || [];
                            const entityRecord = dayData.find(item => item.entity_id === entityId);
                            return entityRecord ? parseInt(entityRecord.neighbor_count) || 0 : null;
                        });

                        // Generate a color for this entity
                        const hue = (index * 137.508) % 360; // Golden angle approximation for good color distribution
                        const color = `hsl(${hue}, 70%, 50%)`;

                        return {
                            label: `Entity ${entityId.substring(0, 8)}...`, // Truncate for readability
                            data: entityData,
                            borderColor: color,
                            backgroundColor: color + '20', // Add transparency
                            fill: false,
                            tension: 0.1,
                            spanGaps: true // Connect points even when there are null values
                        };
                    });
                } else {
                    // For aggregate data (like histogram), show totals per date
                    const neighborCountData = [];
                    const entityCountData = [];

                    sortedDates.forEach(date => {
                        const dayData = dateGroups[date] || [];
                        const totalNeighborCount = dayData.reduce((sum, item) => sum + (parseInt(item.neighbor_count) || 0), 0);
                        const totalEntityCount = dayData.reduce((sum, item) => sum + (parseInt(item.entity_count) || 0), 0);

                        neighborCountData.push(totalNeighborCount);
                        entityCountData.push(totalEntityCount);
                    });

                    datasets = [
                        {
                            label: 'Total Neighbor Count',
                            data: neighborCountData,
                            borderColor: 'rgba(54, 162, 235, 1)',
                            backgroundColor: 'rgba(54, 162, 235, 0.1)',
                            fill: false,
                            tension: 0.1
                        }
                    ];

                    // Add entity count dataset if it has meaningful data
                    if (entityCountData.some(count => count > 0)) {
                        datasets.push({
                            label: 'Total Entity Count',
                            data: entityCountData,
                            borderColor: 'rgba(255, 99, 132, 1)',
                            backgroundColor: 'rgba(255, 99, 132, 0.1)',
                            fill: false,
                            tension: 0.1,
                            yAxisID: 'y1'
                        });
                    }
                }

                chartsByGraph[graphName] = {
                    labels: sortedDates,
                    datasets: datasets
                };
            });

            return chartsByGraph;
        },

        generateTopEntitiesChart() {
            // For top entities, use entity_id as label and neighbor_count as value
            const labels = [];
            const data = [];

            this.reportData.forEach((item, index) => {
                if (item.entity_id && item.neighbor_count) {
                    // Truncate long entity IDs for display
                    const label = item.entity_id.length > 10
                        ? `Entity ${index + 1}`
                        : item.entity_id;
                    labels.push(label);
                    data.push(parseInt(item.neighbor_count) || 0);
                }
            });

            return {
                labels,
                datasets: [{
                    label: 'Neighbor Count',
                    data,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                }]
            };
        },

        generateHistogramChart() {
            // For histogram, create bins based on numeric values
            const labels = [];
            const data = [];

            this.reportData.forEach((item, index) => {
                // Use the first numeric column found
                const numericValue = this.findFirstNumericValue(item);
                if (numericValue !== null) {
                    labels.push(`Item ${index + 1}`);
                    data.push(numericValue);
                }
            });

            return {
                labels,
                datasets: [{
                    label: 'Value',
                    data,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1
                }]
            };
        },

        generateGenericChart() {
            // Generic chart for other report types
            const labels = [];
            const data = [];

            this.reportData.forEach((item, index) => {
                labels.push(`Item ${index + 1}`);
                const numericValue = this.findFirstNumericValue(item);
                data.push(numericValue || 0);
            });

            return {
                labels,
                datasets: [{
                    label: 'Count',
                    data,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1
                }]
            };
        },

        findFirstNumericValue(item) {
            // Find the first numeric value in the object
            for (const [key, value] of Object.entries(item)) {
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && isFinite(numValue)) {
                    return numValue;
                }
            }
            return null;
        },
    },

    computed: {
        ...mapState({
            cid: (state) => state.currentClient.cid,
        }),

        dataTypeOptions() {
            return this.dataTypeMapping[this.reportType] || [
                { label: "Cluster Graph", value: "cluster-graph" }
            ];
        },

        nDaysChartsByGraph() {
            if (!this.reportData?.length || this.metricType !== 'n_days') {
                return {};
            }

            return this.generateNDaysChartsByGraph();
        },

        chartData() {
            if (!this.reportData?.length) {
                return {
                    labels: [],
                    datasets: []
                };
            }

            // Generate chart data based on report type (for non-n_days metrics)
            if (this.reportType === 'top_n_entities') {
                return this.generateTopEntitiesChart();
            } else if (this.reportType === 'histogram') {
                return this.generateHistogramChart();
            } else {
                return this.generateGenericChart();
            }
        },
    },

    async beforeMount() {
        await this.getGraphReport();
    },
}
</script>

<style scoped>
.chart-view {
    height: 40vh !important;
}

.min-col {
    min-width: 16.666667%;
}

.table-container {
    overflow-x: auto;
    width: 100%;
}

.table-container table {
    min-width: 100%;
    white-space: nowrap;
}

.table-card-body {
    padding: 0 !important;
    background-color: #fff;
}
</style>